import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
  IconButton,
  Button,
  Fade,
  Collapse,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FilterListIcon from "@material-ui/icons/FilterList";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import SchoolIcon from "@material-ui/icons/School";
import ClassIcon from "@material-ui/icons/Class";
import LayersOutlinedIcon from "@material-ui/icons/LayersOutlined";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import FitnessCenterOutlinedIcon from "@material-ui/icons/FitnessCenterOutlined";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import TuneIcon from "@material-ui/icons/Tune";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LiveHelpOutlinedIcon from "@material-ui/icons/LiveHelpOutlined";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useLocation } from "react-router-dom";
import { getDashboardPageStyles } from "../../utils/dashboardPageStyles";
import {
  ACTIVITY_CATALOG_TABS,
  canUseActivityCatalog,
  getUserDetails,
  isSuperAdminRole,
  isSchooleRole,
  normalizeId,
  resolveClassLabel,
  resolveSchoolId,
  resolveSchoolLabel,
  tabIndexFromQuery,
} from "../../utils/activityCatalogScope";
import AddClassService from "./Locality/Service/addClassService";
import SchoolService from "./Locality/Service/schoolService";
import SchooleRegistrationService from "./Locality/Service/schooleRegistration";
import SuperActivity from "./SuperActivity";
import ActivityTabel from "./ActivityTabel";
import SubActivity from "./SubActivity";

// /**
//  * ============================================================================
//  * ACTIVITY CATALOG
//  * ============================================================================
//  *
//  * WHAT IT IS
//  * ----------
//  * The Activity Catalog is the place where the Montessori curriculum is defined
//  * as a 3-level hierarchy. This screen is a single wrapper that hosts all three
//  * levels as tabs (see ACTIVITY_CATALOG_TABS), so admins manage the whole tree
//  * from one workflow instead of three separate pages:
//  *
//  *   Step 1  Area Of Work        (SuperActivity)  – top-level curriculum domain
//  *              e.g. "Practical Life", "Sensorial", "Language"
//  *   Step 2  List Of Activities  (ActivityTabel)  – activities inside an area
//  *              e.g. "Pouring", "Spooning" under Practical Life
//  *   Step 3  Exercise            (SubActivity)    – individual exercises/drills
//  *              the concrete tasks a child performs under an activity
//  *
//  * Each child level points to its parent (activity -> area, exercise ->
//  * activity), so the three tabs are meant to be filled in order: create the
//  * area first, then its activities, then the exercises.
//  *
//  * WHO CAN USE IT (scope)
//  * ----------------------
//  * Only SUPERADMIN and SCHOOLE roles (see canUseActivityCatalog); anyone else
//  * sees the "not available" message.
//  *   - SCHOOLE:   their own school is locked/pre-selected. They only manage and
//  *                see entries for their school (plus shared/global entries).
//  *   - SUPERADMIN: sees every school by default and can narrow by School, then
//  *                 Class. Entries they create with no school selected are global
//  *                 (shared) defaults; picking a school scopes the write to it.
//  *
//  * HOW ADDING WORKS
//  * ----------------
//  * 1. Pick the scope at the top: School (superadmin only) then Class. Whatever
//  *    is selected here (`filterSchoolId` / `filterClassId`) is passed down to the
//  *    active tab through the `scope` object, so a new entry is created under that
//  *    same school/class. Superadmin with no school => a global/default entry;
//  *    a school selected => entry belongs to that school (effectiveSchoolIdForWrite).
//  * 2. Switch to the tab for the level you want to add (Area -> Activity -> Exercise).
//  *    The embedded child component (SuperActivity / ActivityTabel / SubActivity)
//  *    renders its own "Add" form and table; this wrapper only supplies the scope
//  *    and the shared class list so the child doesn't refetch it.
//  * 3. When adding an Activity you must choose the parent Area; when adding an
//  *    Exercise you must choose the parent Activity. That is why the order matters:
//  *    the parent level has to exist before its children can be attached.
//  *
//  * State/URL note: the active tab is mirrored in the URL query (?tab=areas|
//  * activities|exercises) via handleTabChange / tabIndexFromQuery, so a tab is
//  * shareable and survives refresh.
//  * ============================================================================
//  */

const TAB_ICONS = [
  <LayersOutlinedIcon />,
  <FormatListBulletedIcon />,
  <FitnessCenterOutlinedIcon />,
];

const useStyles = makeStyles((theme) => ({
  ...getDashboardPageStyles(theme),
  pageShell: {
    minHeight: "calc(100vh - 120px)",
    background: "linear-gradient(165deg, #f0f5fc 0%, #f8fafc 42%, #ffffff 100%)",
    borderRadius: 20,
    padding: theme.spacing(2.5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      borderRadius: 12,
    },
  },
  heroCard: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 18,
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(3, 3.5),
    background: "linear-gradient(135deg, #0f3b62 0%, #1769d6 52%, #1a7fe8 100%)",
    boxShadow: "0 12px 40px rgba(15, 59, 98, 0.22)",
    color: "#fff",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2.5, 2),
    },
  },
  heroGlow: {
    position: "absolute",
    width: 280,
    height: 280,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(255,255,255,0.14) 0%, transparent 70%)",
    top: -80,
    right: -40,
    pointerEvents: "none",
  },
  heroTitle: {
    fontWeight: 800,
    fontSize: "2rem",
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.55rem",
    },
  },
  heroSubtitle: {
    marginTop: theme.spacing(0.75),
    fontSize: "0.95rem",
    color: "rgba(255,255,255,0.88)",
    maxWidth: 560,
    lineHeight: 1.5,
  },
  heroMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  heroChip: {
    backgroundColor: "rgba(255,255,255,0.16)",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.78rem",
    height: 28,
    border: "1px solid rgba(255,255,255,0.22)",
    backdropFilter: "blur(4px)",
  },
  noteBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 14,
    backgroundColor: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(255,255,255,0.5)",
    marginBottom: theme.spacing(2),
    boxShadow: "0 4px 16px rgba(15, 59, 98, 0.08)",
  },
  noteIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 10,
    background: "linear-gradient(135deg, #1769d6, #0f3b62)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  catalogCard: {
    borderRadius: 18,
    border: "1px solid #e3e9f2",
    boxShadow: "0 8px 32px rgba(16, 42, 67, 0.08)",
    overflow: "visible",
    backgroundColor: "#fff",
  },
  filterStrip: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    background: "linear-gradient(180deg, #fafbfd 0%, #ffffff 100%)",
    borderBottom: "1px solid #eef1f6",
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
  },
  filterStripTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 700,
    color: "#0f2d4d",
    fontSize: "0.88rem",
    marginBottom: 10,
  },
  filterField: {
    minWidth: 240,
    flex: "1 1 220px",
    maxWidth: 360,
  },
  selectRoot: {
    borderRadius: 10,
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#dce3ee",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#b8c9e0",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1769d6",
      borderWidth: 2,
    },
  },
  activeFiltersRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2.5, 1.5),
    backgroundColor: "#fafbfd",
    borderBottom: "1px solid #eef1f6",
  },
  activeChip: {
    backgroundColor: "#eef4ff",
    color: "#1769d6",
    fontWeight: 600,
    height: 26,
    fontSize: "0.75rem",
  },
  tabSection: {
    padding: theme.spacing(2.5, 2.5, 2),
    backgroundColor: "#f8fafc",
    borderBottom: "1px solid #eef1f6",
    overflow: "visible",
  },
  tabRail: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    overflowX: "auto",
    overflowY: "visible",
    padding: theme.spacing(1, 0.5),
    margin: theme.spacing(-0.5, 0),
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": { height: 6 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#d0d9e6",
      borderRadius: 3,
    },
  },
  tabPillSlot: {
    flex: "1 1 0",
    minWidth: 200,
    padding: theme.spacing(0.75, 0),
    [theme.breakpoints.down("sm")]: {
      minWidth: 168,
    },
  },
  tabPill: {
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.5, 1.75),
    border: "2px solid #e3e9f2",
    borderRadius: 14,
    backgroundColor: "#fff",
    cursor: "pointer",
    textAlign: "left",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
    position: "relative",
    "&:hover": {
      borderColor: "#b8c9e0",
      boxShadow: "0 4px 16px rgba(23, 105, 214, 0.1)",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.25, 1.25),
    },
  },
  tabPillActive: {
    borderColor: "#1769d6",
    background: "linear-gradient(145deg, #ffffff 0%, #f0f6ff 100%)",
    boxShadow: "0 0 0 1px rgba(23, 105, 214, 0.08), 0 6px 22px rgba(23, 105, 214, 0.16)",
    zIndex: 1,
    "& $tabStepBadge": {
      background: "linear-gradient(135deg, #1769d6, #0f3b62)",
      color: "#fff",
    },
    "& $tabIconWrap": {
      background: "linear-gradient(135deg, #1769d6, #1a8cff)",
      color: "#fff",
      boxShadow: "0 4px 12px rgba(23, 105, 214, 0.3)",
    },
    "& $tabLabel": {
      color: "#0f2d4d",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      height: 4,
      borderRadius: "0 0 12px 12px",
      background: "linear-gradient(90deg, #1769d6, #4da3ff)",
    },
  },
  tabStepBadge: {
    flexShrink: 0,
    width: 22,
    height: 22,
    borderRadius: 7,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.7rem",
    fontWeight: 800,
    backgroundColor: "#eef1f6",
    color: "#6b7c93",
    marginTop: 2,
  },
  tabIconWrap: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0f4fa",
    color: "#5a6d85",
    transition: "all 0.22s ease",
    "& svg": { fontSize: 22 },
  },
  tabTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  tabLabel: {
    display: "block",
    fontWeight: 700,
    fontSize: "0.9rem",
    color: "#3d4f66",
    lineHeight: 1.3,
    transition: "color 0.2s ease",
  },
  tabDesc: {
    display: "block",
    marginTop: 2,
    fontSize: "0.72rem",
    color: "#8a97a8",
    lineHeight: 1.35,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  tabConnector: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: "50%",
    color: "#9aabc0",
    flexShrink: 0,
    backgroundColor: "#eef2f7",
    "& svg": { fontSize: 18 },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  tabPanel: {
    padding: theme.spacing(2.5),
    backgroundColor: "#fcfdfe",
    minHeight: 320,
    borderBottomLeftRadius: 18,
    borderBottomRightRadius: 18,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  resetBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    color: "#5a6d85",
    border: "1px solid #dce3ee",
    padding: "6px 14px",
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: "#f5f8fc",
      borderColor: "#b8c9e0",
    },
  },
  filterLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#5a6d85",
    marginBottom: 6,
    display: "flex",
    alignItems: "center",
    gap: 6,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  helpCard: {
    borderRadius: 16,
    border: "1px solid #e3e9f2",
    backgroundColor: "#fff",
    boxShadow: "0 6px 24px rgba(16, 42, 67, 0.06)",
    marginBottom: theme.spacing(2.5),
    overflow: "hidden",
  },
  helpHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.75, 2.25),
    background: "linear-gradient(180deg, #fafbfd 0%, #ffffff 100%)",
    borderBottom: "1px solid #eef1f6",
  },
  helpHeaderIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "linear-gradient(135deg, #1769d6, #0f3b62)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  helpItem: {
    borderBottom: "1px solid #eef1f6",
    "&:last-child": { borderBottom: "none" },
  },
  helpQuestion: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.75, 2.25),
    border: "none",
    backgroundColor: "#fff",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
    outline: "none",
    transition: "background 0.2s ease",
    "&:hover": { backgroundColor: "#f6f9fe" },
  },
  helpQuestionActive: {
    backgroundColor: "#f0f6ff",
  },
  helpQIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 9,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#eef4ff",
    color: "#1769d6",
    flexShrink: 0,
    "& svg": { fontSize: 18 },
  },
  helpQText: {
    flex: 1,
    fontWeight: 700,
    fontSize: "0.92rem",
    color: "#0f2d4d",
    letterSpacing: "0.01em",
  },
  helpChevron: {
    color: "#8a97a8",
    transition: "transform 0.25s ease",
    flexShrink: 0,
  },
  helpChevronOpen: {
    transform: "rotate(180deg)",
    color: "#1769d6",
  },
  helpAnswer: {
    padding: theme.spacing(0, 2.25, 2, 6.75),
    color: "#46566b",
    fontSize: "0.86rem",
    lineHeight: 1.6,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0, 2, 2, 2.25),
    },
  },
  helpStepList: {
    margin: theme.spacing(0.5, 0, 0),
    paddingLeft: theme.spacing(2.25),
    "& li": { marginBottom: theme.spacing(0.75) },
  },
}));

export default function ActivityCatalog() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const userDetails = useMemo(() => getUserDetails(), []);
  const isSuperAdmin = isSuperAdminRole(userDetails);
  const isSchoole = isSchooleRole(userDetails);
  const canAccess = canUseActivityCatalog(userDetails);
  const lockedSchoolId = isSchoole ? resolveSchoolId(userDetails) : "";

  const [tab, setTab] = useState(() => tabIndexFromQuery(location.search));
  const [filterSchoolId, setFilterSchoolId] = useState(lockedSchoolId);
  const [filterClassId, setFilterClassId] = useState("");
  const [schoolList, setSchoolList] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [classList, setClassList] = useState([]);
  const [noteOpen, setNoteOpen] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (key) => setOpenFaq((prev) => (prev === key ? null : key));

  useEffect(() => {
    setTab(tabIndexFromQuery(location.search));
  }, [location.search]);

  useEffect(() => {
    if (lockedSchoolId) {
      setFilterSchoolId(lockedSchoolId);
    }
  }, [lockedSchoolId]);

  const handleTabChange = (nextTab) => {
    setTab(nextTab);
    const tabKey = ACTIVITY_CATALOG_TABS[nextTab]?.key || "areas";
    history.replace(`/app/activity-catalog?tab=${tabKey}`);
  };

  const loadSchools = useCallback(async () => {
    if (isSchoole && lockedSchoolId) {
      try {
        const school = await SchooleRegistrationService.getSchoolById(lockedSchoolId);
        setSchoolList(school ? [school] : []);
      } catch {
        setSchoolList([]);
      }
      return;
    }

    if (!isSuperAdmin) return;

    setSchoolsLoading(true);
    try {
      let rows = await SchoolService.getAllSchoolList();
      rows = Array.isArray(rows) ? rows : [];
      if (!rows.length) {
        const fallback = await SchooleRegistrationService.getAllSchooleRegistration().catch(() => []);
        rows = Array.isArray(fallback) ? fallback : [];
      }
      setSchoolList(rows);
    } catch {
      setSchoolList([]);
    } finally {
      setSchoolsLoading(false);
    }
  }, [isSuperAdmin, isSchoole, lockedSchoolId]);

  const loadClasses = useCallback(() => {
    const listId = filterSchoolId || (isSuperAdmin ? "all" : lockedSchoolId);
    if (!listId) {
      setClassList([]);
      return;
    }
    const role = isSuperAdmin ? "SUPERADMIN" : isSchoole ? "SCHOOLE" : undefined;
    AddClassService.getAllAddClass(listId, role)
      .then((res) => {
        const rows = Array.isArray(res) ? res : [];
        setClassList(rows);
        if (filterClassId && !rows.some((c) => String(c._id) === String(filterClassId))) {
          setFilterClassId("");
        }
      })
      .catch(() => setClassList([]));
  }, [filterSchoolId, filterClassId, isSuperAdmin, isSchoole, lockedSchoolId]);

  useEffect(() => {
    loadSchools();
  }, [loadSchools]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const scope = useMemo(
    () => ({
      filterSchoolId,
      filterClassId,
      userDetails,
      isSuperAdmin,
      isSchoole,
    }),
    [filterSchoolId, filterClassId, userDetails, isSuperAdmin, isSchoole]
  );

  const showSchoolOnClassOptions = isSuperAdmin && !filterSchoolId;

  const selectedSchoolLabel = useMemo(() => {
    if (!filterSchoolId) return null;
    const match = schoolList.find((s) => normalizeId(s._id) === String(filterSchoolId));
    return match ? resolveSchoolLabel(match) : null;
  }, [filterSchoolId, schoolList]);

  const selectedClassLabel = useMemo(() => {
    if (!filterClassId) return null;
    const match = classList.find((c) => String(c._id) === String(filterClassId));
    return match ? resolveClassLabel(match, showSchoolOnClassOptions) : null;
  }, [filterClassId, classList, showSchoolOnClassOptions]);

  const hasActiveFilters = Boolean(
    (isSuperAdmin && !filterSchoolId) || filterSchoolId || filterClassId
  );

  const handleResetFilters = () => {
    setFilterSchoolId(lockedSchoolId || "");
    setFilterClassId("");
  };

  const faqItems = [
    {
      key: "what",
      icon: <LiveHelpOutlinedIcon />,
      question: "1. What it is",
      answer: (
        <>
          <Typography component="p" style={{ fontSize: "0.86rem", color: "#46566b", lineHeight: 1.6 }}>
            The Activity Catalog is where the Montessori curriculum is defined as a{" "}
            <strong> Up to 3-level hierarchy</strong>. One screen hosts Up to three levels as tabs, so you
            manage the whole tree from a single workflow:
          </Typography>
          <ul className={classes.helpStepList}>
            <li>
              <strong>Step 1 · Area of Work</strong> — the top-level curriculum domain
              (e.g. "Practical Life", "Sensorial", "Language").
            </li>
            <li>
              <strong>Step 2 · List of Activities</strong> — activities inside an area
              (e.g. "Pouring", "Spooning" under Practical Life).
            </li>
            <li>
              <strong>Step 3 · Exercise (Primary and Toddler only)</strong> — the individual exercises/drills a child performs
              under an activity.
            </li>
          </ul>
          <Typography component="p" style={{ fontSize: "0.86rem", color: "#46566b", lineHeight: 1.6, marginTop: 8 }}>
             Tabs are filled in order:
            create the area first, then its activities, then the exercises.
          </Typography>
        </>
      ),
    },
    {
      key: "who",
      icon: <PeopleOutlineIcon />,
      question: "2. Who can use it",
      answer: (
        <>
          <Typography component="p" style={{ fontSize: "0.86rem", color: "#46566b", lineHeight: 1.6 }}>
            <strong>School</strong> accounts can open the
            Activity Catalog 
          </Typography>
          <ul className={classes.helpStepList}>
            <li>
            School Admins and Principals can access only their own school's data
            </li>
           
          </ul>
        </>
      ),
    },
    {
      key: "add",
      icon: <AddCircleOutlineIcon />,
      question: "3. How adding works",
      answer: (
        <ol className={classes.helpStepList}>
          <li>
          Pre-built activities are already available in the system. These activities cannot be edited or deleted.
          </li>
          <li>
            Switch to the tab for the level you want to add
            (<strong>Area → Activity → Exercise</strong>) and use its "Add" form.
          </li>
          <li>
          Whatever Activity or Exercise is added will be automatically reflected in the Teacher Activity Planner.
          </li>
        </ol>
      ),
    },
  ];

  if (!canAccess) {
    return (
      <Box className={classes.pageShell}>
        <Typography className={classes.pageTitle}>Activity Catalog</Typography>
        <Typography className={classes.pageSubtitle} style={{ marginTop: 8 }}>
          Activity Catalog is available for school and super admin accounts.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.pageShell}>
      <Box className={classes.heroCard}>
        <Box className={classes.heroGlow} />
        <Typography className={classes.heroTitle}>Activity Catalog</Typography>
        <Typography className={classes.heroSubtitle}>
          {isSuperAdmin
            ? "Curate area of work, activities, and exercises across every school — one structured workflow."
            : "Build and manage your school's full activity hierarchy from areas down to individual exercises."}
        </Typography>
        <Box className={classes.heroMeta}>
          <Chip
            size="small"
            icon={<LayersOutlinedIcon style={{ color: "rgba(255,255,255,0.9)", fontSize: 16 }} />}
            label="Up to 3-level hierarchy"
            className={classes.heroChip}
          />
          <Chip
            size="small"
            icon={<TuneIcon style={{ color: "rgba(255,255,255,0.9)", fontSize: 16 }} />}
            label={isSuperAdmin ? "Multi-school scope" : "School-scoped"}
            className={classes.heroChip}
          />
        </Box>
      </Box>

      {noteOpen && (
        <Box className={classes.noteBanner}>
          <Box className={classes.noteIconWrap}>
            <InfoOutlinedIcon style={{ fontSize: 18 }} />
          </Box>
          <Box flex={1}>
            <Typography style={{ fontWeight: 700, color: "#0f2d4d", fontSize: "0.9rem" }}>
              {isSuperAdmin ? "Default catalog view" : "Your school catalog"}
            </Typography>
            <Typography style={{ color: "#5a6d85", fontSize: "0.85rem", marginTop: 3, lineHeight: 1.45 }}>
              {isSuperAdmin
                ? "All schools are included by default. Narrow with School, then Class — your selection applies to every tab below."
                : "Your school is pre-selected. Choose a class to focus the catalog, then switch tabs to manage each level."}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setNoteOpen(false)} aria-label="Dismiss tip">
            <CloseIcon style={{ fontSize: 18, color: "#8a97a8" }} />
          </IconButton>
        </Box>
      )}

      <Box className={classes.helpCard}>
        <Box className={classes.helpHeader}>
          <Box className={classes.helpHeaderIcon}>
            <HelpOutlineIcon style={{ fontSize: 18 }} />
          </Box>
          <Box flex={1}>
            <Typography style={{ fontWeight: 700, color: "#0f2d4d", fontSize: "0.95rem" }}>
              Quick guide
            </Typography>
            <Typography style={{ color: "#8a97a8", fontSize: "0.8rem", marginTop: 1 }}>
              Tap a question to learn how the Activity Catalog works.
            </Typography>
          </Box>
        </Box>
        {faqItems.map((item) => {
          const isOpen = openFaq === item.key;
          return (
            <Box key={item.key} className={classes.helpItem}>
              <button
                type="button"
                aria-expanded={isOpen}
                className={`${classes.helpQuestion} ${isOpen ? classes.helpQuestionActive : ""}`}
                onClick={() => toggleFaq(item.key)}
              >
                <span className={classes.helpQIconWrap}>{item.icon}</span>
                <span className={classes.helpQText}>{item.question}</span>
                <ExpandMoreIcon
                  className={`${classes.helpChevron} ${isOpen ? classes.helpChevronOpen : ""}`}
                />
              </button>
              <Collapse in={isOpen} timeout={260} unmountOnExit>
                <Box className={classes.helpAnswer}>{item.answer}</Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      <Card elevation={0} className={classes.catalogCard}>
        <Box className={classes.filterStrip}>
          <Box display="flex" flexWrap="wrap" gridGap={16} flex={1}>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.filterField}
              disabled={isSchoole || schoolsLoading}
            >
              <Typography className={classes.filterLabel}>
                <SchoolIcon style={{ fontSize: 15, color: "#1769d6" }} /> School
              </Typography>
              <Select
                className={classes.selectRoot}
                value={filterSchoolId}
                onChange={(e) => {
                  setFilterSchoolId(e.target.value);
                  setFilterClassId("");
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <em style={{ color: "#8a97a8" }}>All Schools</em>;
                  }
                  const match = schoolList.find((s) => normalizeId(s._id) === String(selected));
                  return match ? resolveSchoolLabel(match) : String(selected);
                }}
              >
                {isSuperAdmin && (
                  <MenuItem value="">
                    <em>All Schools</em>
                  </MenuItem>
                )}
                {schoolsLoading && (
                  <MenuItem disabled value="__loading__">
                    <CircularProgress size={18} style={{ marginRight: 8 }} /> Loading schools…
                  </MenuItem>
                )}
                {!schoolsLoading && schoolList.length === 0 && (
                  <MenuItem disabled value="__empty__">
                    No schools found
                  </MenuItem>
                )}
                {schoolList.map((school) => {
                  const id = normalizeId(school._id);
                  return (
                    <MenuItem key={id} value={id}>
                      {resolveSchoolLabel(school)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" className={classes.filterField}>
              <Typography className={classes.filterLabel}>
                <ClassIcon style={{ fontSize: 15, color: "#1769d6" }} /> Class
              </Typography>
              <Select
                className={classes.selectRoot}
                value={filterClassId}
                onChange={(e) => setFilterClassId(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>All Classes</em>
                </MenuItem>
                {classList.map((cls) => (
                  <MenuItem key={cls._id} value={cls._id}>
                    {resolveClassLabel(cls, showSchoolOnClassOptions)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Button size="small" className={classes.resetBtn} onClick={handleResetFilters} startIcon={<FilterListIcon />}>
            Reset
          </Button>
        </Box>

        {hasActiveFilters && (
          <Box className={classes.activeFiltersRow}>
            <Typography variant="caption" style={{ color: "#8a97a8", fontWeight: 600, marginRight: 4 }}>
              Scope:
            </Typography>
            {isSuperAdmin && !filterSchoolId && (
              <Chip size="small" label="All schools" className={classes.activeChip} />
            )}
            {selectedSchoolLabel && (
              <Chip size="small" icon={<SchoolIcon style={{ fontSize: 14 }} />} label={selectedSchoolLabel} className={classes.activeChip} />
            )}
            {selectedClassLabel ? (
              <Chip size="small" icon={<ClassIcon style={{ fontSize: 14 }} />} label={selectedClassLabel} className={classes.activeChip} />
            ) : (
              <Chip size="small" label="All classes" className={classes.activeChip} variant="outlined" style={{ borderColor: "#d6e4ff", color: "#6b7c93" }} />
            )}
          </Box>
        )}

        <Box className={classes.tabSection}>
          <Box className={classes.tabRail} role="tablist" aria-label="Activity catalog sections">
            {ACTIVITY_CATALOG_TABS.map((t, idx) => (
              <React.Fragment key={t.key}>
                <Box className={classes.tabPillSlot}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === idx}
                    className={`${classes.tabPill} ${tab === idx ? classes.tabPillActive : ""}`}
                    onClick={() => handleTabChange(idx)}
                  >
                    <span className={classes.tabStepBadge}>{t.step}</span>
                    <span className={classes.tabIconWrap}>{TAB_ICONS[idx]}</span>
                    <span className={classes.tabTextBlock}>
                      <span className={classes.tabLabel}>{t.label}</span>
                      <span className={classes.tabDesc}>{t.description}</span>
                    </span>
                  </button>
                </Box>
                {idx < ACTIVITY_CATALOG_TABS.length - 1 && (
                  <span className={classes.tabConnector} aria-hidden>
                    <ChevronRightIcon />
                  </span>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        <Box className={classes.tabPanel} role="tabpanel">
          <Fade in key={tab} timeout={280}>
            <Box>
              {tab === 0 && (
                <SuperActivity embedded scope={scope} sharedClassList={classList} />
              )}
              {tab === 1 && (
                <ActivityTabel embedded scope={scope} sharedClassList={classList} />
              )}
              {tab === 2 && (
                <SubActivity embedded scope={scope} sharedClassList={classList} />
              )}
            </Box>
          </Fade>
        </Box>
      </Card>
    </Box>
  );
}
